import { useCallback } from 'react';

import { useIntl } from 'react-intl-next';

import { getATLContextUrl } from '@atlassian/atl-context';
import {
	useAgentFavourites,
	useAgentUrlActions,
	useDeleteAgent,
} from '@atlassian/conversation-assistant-agent';
import { addRovoParamsToUrl } from '@atlassian/conversation-assistant-pubsub';
import { usePeopleTeamAnalyticsEvents } from '@atlassian/people-teams-ui/analytics';
import { AnalyticsEventSource } from '@atlassian/ptc-common';

import { useFlags, useRouter } from '../AppConfigContext';

import { messages } from './messages';

type UseAgentActionsProps = {
	product: string;
	cloudId: string;
	agentId?: string;
	agentName?: string;
	agentIdentityAccountId?: string | null | undefined;
};

export const useAgentActions = ({
	product,
	cloudId,
	agentId,
	agentName,
	agentIdentityAccountId,
}: UseAgentActionsProps) => {
	const addFlag = useFlags();
	const { formatMessage } = useIntl();
	const { push } = useRouter();

	const { favouriteAgent, unfavouriteAgent } = useAgentFavourites({
		experienceId: 'profile-page',
		product,
	});

	const { onEditAgent, onCopyAgent, onDuplicateAgent } = useAgentUrlActions({ siteId: cloudId });
	const { deleteAgent: deleteRovoAgent } = useDeleteAgent({
		experienceId: 'profile-page',
		product,
	});

	const setFavouriteAgent = useCallback(
		async (isFavourite: boolean, onSuccess?: () => void) => {
			if (!agentId) {
				return;
			}
			if (isFavourite) {
				return await favouriteAgent.mutate(agentId, { onSuccess });
			} else {
				return await unfavouriteAgent.mutate(agentId, { onSuccess });
			}
		},
		[agentId, favouriteAgent, unfavouriteAgent],
	);

	const { fireEvent } = usePeopleTeamAnalyticsEvents();

	const editAgent = () => {
		if (!agentId) {
			return;
		}
		fireEvent('ui.editAgent.clicked', {});
		onEditAgent(agentId);
	};
	const copyAgent = () => {
		if (!agentId) {
			return;
		}
		fireEvent('ui.copyAgent.clicked', {});
		onCopyAgent({
			agentId,
			agentIdentityAccountId,
		});
	};
	const duplicateAgent = () => {
		if (!agentId) {
			return;
		}
		fireEvent('ui.duplicateAgent.clicked', {});

		onDuplicateAgent(agentId);
	};

	const deleteAgent = () => {
		if (!agentId) {
			return;
		}
		fireEvent('ui.deleteAgent.clicked', {});

		deleteRovoAgent.mutate(agentId, {
			onSuccess: () => {
				addFlag(
					{
						title: formatMessage(messages.agentDeletedSuccessFlagTitle),
						description: formatMessage(messages.agentDeletedSuccessFlagDescription, {
							agentName,
						}),
						type: 'success',
						id: 'ptc-directory.agent-profile.delete-agent-success',
					},
					{
						sendAnalytics: true,
						name: AnalyticsEventSource.AGENT_DELETE_SUCCESS_FLAG,
					},
				);
				setTimeout(() => {
					push('search');
				}, 100);
			},
			onError: () => {
				addFlag(
					{
						title: formatMessage(messages.agentDeletedErrorFlagTitle),
						description: formatMessage(messages.agentDeletedErrorFlagDescription),
						type: 'error',
						id: 'ptc-directory.agent-profile.delete-agent-error',
					},
					{
						sendAnalytics: true,
						name: AnalyticsEventSource.AGENT_DELETE_ERROR_FLAG,
					},
				);
			},
		});
	};

	const onChatWithAgent = () => {
		if (!agentId) {
			return;
		}
		fireEvent('ui.chatWithAgent.clicked', {});

		window.open(
			addRovoParamsToUrl(`${getATLContextUrl('home')}/chat`, {
				pathway: 'chat',
				cloudId,
				agentId,
			}),
			'_blank',
			'noopener, noreferrer',
		);
	};

	const startConversation = (prompt: string) => {
		if (!agentId) {
			return;
		}
		fireEvent('ui.startConversationWithAgent.clicked', {});
		window.open(
			addRovoParamsToUrl(`${getATLContextUrl('home')}/chat`, {
				pathway: 'chat',
				prompt,
				cloudId,
				agentId,
			}),
			'_blank',
			'noopener, noreferrer',
		);
	};

	return {
		setFavouriteAgent,
		editAgent,
		copyAgent,
		duplicateAgent,
		deleteAgent,
		onChatWithAgent,
		startConversation,
	};
};
